import { useEffect, useRef, useCallback } from 'react';
import { useOAuth } from 'providers/OAuthProvider';
import { useToastMessage } from '@jouri/components';
import { useTranslation } from 'react-i18next';

const IDLE_TIMEOUT = 15 * 60 * 1000;

/**
 * Hook to automatically logout user after 15 minutes of inactivity
 * Tracks mouse movements, clicks, keyboard events, touch events, and scroll events
 */
const useIdleTimeout = () => {
    const { logout, isAuthenticated } = useOAuth();
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const isAuthenticatedRef = useRef(isAuthenticated);
    const { send } = useToastMessage();
    const { t } = useTranslation();

    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    const handleLogout = useCallback(() => {
        if (isAuthenticatedRef.current) {
            send(t('general_session_expired_message'), 'error');
            logout();
        }
    }, [logout]);

    const resetTimer = useCallback(() => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }

        if (isAuthenticatedRef.current) {
            timeoutIdRef.current = setTimeout(handleLogout, IDLE_TIMEOUT);
        }
    }, [handleLogout]);

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click',
        ];

        const handleActivity = () => {
            resetTimer();
        };

        events.forEach((event) => {
            document.addEventListener(event, handleActivity, true);
        });

        resetTimer();

        return () => {
            events.forEach((event) => {
                document.removeEventListener(event, handleActivity, true);
            });

            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        };
    }, [isAuthenticated, resetTimer]);

    return null;
};

export default useIdleTimeout;
